import React, { useEffect, useState } from 'react';
import { FormInput } from './components/FormInput';
import { FormSelect } from './components/FormSelect';
import { FormCard } from './components/FormCard';
import { Send, User, MapPin, Briefcase } from 'lucide-react';
import { sriLankaData } from './data/sriLankaData';
// Types for form state
interface FormData {
  firstName: string;
  surname: string;
  nationalId: string;
  dob: string;
  phoneNumber: string;
  address: string;
  province: string;
  district: string;
  electorate: string;
  divisionalSecretariat: string;
  gramaNiladhari: string;
  divisionalSecretariatText: string;
  occupation: string;
}
interface FormErrors {
  [key: string]: string;
}
interface FormTouched {
  [key: string]: boolean;
}
export function App() {
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    surname: '',
    nationalId: '',
    dob: '',
    phoneNumber: '',
    address: '',
    province: '',
    district: '',
    electorate: '',
    divisionalSecretariat: '',
    gramaNiladhari: '',
    divisionalSecretariatText: '',
    occupation: ''
  });
  const [touched, setTouched] = useState<FormTouched>({});
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Derived options for cascading dropdowns
  const provinceOptions = Object.keys(sriLankaData).map(p => ({
    value: p,
    label: p
  }));
  const districtOptions = formData.province ? Object.keys(sriLankaData[formData.province] || {}).map(d => ({
    value: d,
    label: d
  })) : [];
  const electorateOptions = formData.province && formData.district ? Object.keys(sriLankaData[formData.province]?.[formData.district] || {}).map(e => ({
    value: e,
    label: e
  })) : [];
  const dsOptions = formData.province && formData.district && formData.electorate ? (sriLankaData[formData.province]?.[formData.district]?.[formData.electorate] || []).map(ds => ({
    value: ds,
    label: ds
  })) : [];
  // Validation Logic
  const validateField = (name: string, value: string): string => {
    switch (name) {
      case 'firstName':
        return !value.trim() ? 'First name is required' : '';
      case 'surname':
        return !value.trim() ? 'Surname is required' : '';
      case 'nationalId':
        if (!value.trim()) return 'National ID is required';
        const trimmedNic = value.trim();
        // Old NIC format: 9 digits followed by V or X (case-insensitive)
        const oldNicPattern = /^\d{9}[VvXx]$/;
        // New NIC format: exactly 12 digits
        const newNicPattern = /^\d{12}$/;
        if (!oldNicPattern.test(trimmedNic) && !newNicPattern.test(trimmedNic)) {
          return 'Enter a valid NIC (e.g. 123456789V or 200012345678)';
        }
        return '';
      case 'dob':
        if (!value) return 'Date of birth is required';
        const date = new Date(value);
        const now = new Date();
        return date > now ? 'Date cannot be in the future' : '';
      case 'phoneNumber':
        if (!value.trim()) return 'Phone number is required';
        const digitsOnly = value.replace(/\D/g, '');
        return digitsOnly.length !== 10 ? 'Phone number must be exactly 10 digits' : '';
      case 'address':
        return value.trim().length < 5 ? 'Please enter a valid address' : '';
      case 'province':
        return !value ? 'Province is required' : '';
      case 'district':
        return !value ? 'District is required' : '';
      case 'electorate':
        return !value ? 'Electorate is required' : '';
      case 'divisionalSecretariat':
        return !value ? 'Divisional Secretariat is required' : '';
      case 'gramaNiladhari':
      case 'divisionalSecretariatText':
        return '';
      // Optional fields
      case 'occupation':
        return !value.trim() ? 'Occupation is required' : '';
      default:
        return '';
    }
  };
  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    const error = validateField(name, value);
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: value
      };
      // Handle cascading resets
      if (name === 'province') {
        newData.district = '';
        newData.electorate = '';
        newData.divisionalSecretariat = '';
      } else if (name === 'district') {
        newData.electorate = '';
        newData.divisionalSecretariat = '';
      } else if (name === 'electorate') {
        newData.divisionalSecretariat = '';
      }
      return newData;
    });
    // Clear error immediately when user starts typing/selecting if field was touched
    if (touched[name]) {
      const error = validateField(name, value);
      setErrors(prev => {
        const newErrors = {
          ...prev,
          [name]: error
        };
        // Clear child errors on parent change
        if (name === 'province') {
          delete newErrors.district;
          delete newErrors.electorate;
          delete newErrors.divisionalSecretariat;
        } else if (name === 'district') {
          delete newErrors.electorate;
          delete newErrors.divisionalSecretariat;
        } else if (name === 'electorate') {
          delete newErrors.divisionalSecretariat;
        }
        return newErrors;
      });
    }
  };
  // Section Validation Status
  const isPersonalValid = formData.firstName.trim() !== '' && formData.surname.trim() !== '' && formData.nationalId.trim() !== '' && formData.dob !== '' && formData.phoneNumber.trim() !== '' && !errors.firstName && !errors.surname && !errors.nationalId && !errors.dob && !errors.phoneNumber;
  const isLocationValid = formData.address.trim().length >= 5 && !errors.address && formData.province !== '' && !errors.province && formData.district !== '' && !errors.district && formData.electorate !== '' && !errors.electorate && formData.divisionalSecretariat !== '' && !errors.divisionalSecretariat;
  const isProfessionalValid = formData.occupation.trim() !== '' && !errors.occupation;
  const isFormValid = isPersonalValid && isLocationValid && isProfessionalValid;
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid) {
      // Touch all fields to show errors
      const allTouched = Object.keys(formData).reduce((acc, key) => ({
        ...acc,
        [key]: true
      }), {});
      setTouched(allTouched);
      // Validate all fields
      const allErrors: FormErrors = {};
      Object.keys(formData).forEach(key => {
        const error = validateField(key, formData[key as keyof FormData]);
        if (error) allErrors[key] = error;
      });
      setErrors(allErrors);
      return;
    }
    setIsSubmitting(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsSubmitting(false);
    alert('Form submitted successfully!');
  };
  return <div className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8 font-sans text-slate-900 relative" data-id="element-0">
      {/* Background Logo Watermark */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none overflow-hidden" data-id="element-1">
        <img src="https://cdn.magicpatterns.com/uploads/6TnPYx5Y9ejZaC14f433Gb/IMG_1660(1).jpg" alt="Background Logo" className="w-96 h-96 object-contain opacity-[0.08]" data-id="element-2" />
      </div>

      <div className="max-w-2xl mx-auto space-y-8 relative z-10" data-id="element-3">
        {/* Header */}
        <div className="text-center space-y-4 mb-10" data-id="element-4">
          <img src="https://cdn.magicpatterns.com/uploads/6TnPYx5Y9ejZaC14f433Gb/IMG_1660(1).jpg" alt="Sri Lanka Peoples Youth Front Logo" className="w-32 h-32 mx-auto object-contain" data-id="element-5" />
          <h1 className="text-3xl font-bold tracking-tight text-slate-900" data-id="element-6">
            SLPP Youth Registration
          </h1>
          <p className="text-slate-500 text-lg max-w-md mx-auto" data-id="element-7">
            Please complete all sections below to create your professional
            profile.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" data-id="element-8">
          {/* Section 1: Personal Info */}
          <FormCard title="Personal Information" isValid={isPersonalValid} data-id="element-9">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-10">
              <FormInput id="firstName" name="firstName" label="First Name / මුල් නම" placeholder="e.g. Jane" value={formData.firstName} onChange={handleChange} onBlur={handleBlur} error={touched.firstName ? errors.firstName : undefined} data-id="element-11" />
              <FormInput id="surname" name="surname" label="Surname / වාසගම" placeholder="e.g. Doe" value={formData.surname} onChange={handleChange} onBlur={handleBlur} error={touched.surname ? errors.surname : undefined} data-id="element-12" />
            </div>
            <FormInput id="nationalId" name="nationalId" label="National ID | ජාතික හැඳුනුම්පත" placeholder="e.g. 123456789V or 200012345678" value={formData.nationalId} onChange={handleChange} onBlur={handleBlur} error={touched.nationalId ? errors.nationalId : undefined} data-id="element-13" />
            <FormInput id="dob" name="dob" type="date" label="Date of Birth / උපන් දිනය" value={formData.dob} onChange={handleChange} onBlur={handleBlur} error={touched.dob ? errors.dob : undefined} data-id="element-14" />
            <FormInput id="phoneNumber" name="phoneNumber" type="tel" label="Phone Number / දුරකථන අංකය" placeholder="e.g. 0771234567" value={formData.phoneNumber} onChange={handleChange} onBlur={handleBlur} error={touched.phoneNumber ? errors.phoneNumber : undefined} data-id="element-15" />
          </FormCard>

          {/* Section 2: Location */}
          <FormCard title="Location Details" isValid={isLocationValid} data-id="element-16">
            <div className="space-y-5" data-id="element-17">
              <FormInput id="address" name="address" label="Full Address / ලිපිනය" placeholder="123 Main St, City, Country" value={formData.address} onChange={handleChange} onBlur={handleBlur} error={touched.address ? errors.address : undefined} data-id="element-18" />

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-19">
                <FormSelect id="province" name="province" label="Province | පළාත" placeholder="Select Province" options={provinceOptions} value={formData.province} onChange={handleChange} onBlur={handleBlur} error={touched.province ? errors.province : undefined} data-id="element-20" />

                <FormSelect id="district" name="district" label="District | දිස්ත්‍රික්කය" placeholder="Select District" options={districtOptions} value={formData.district} onChange={handleChange} onBlur={handleBlur} disabled={!formData.province} error={touched.district ? errors.district : undefined} data-id="element-21" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5" data-id="element-22">
                <FormSelect id="electorate" name="electorate" label="Electorate | ඡන්ද කොට්ඨාශ" placeholder="Select Electorate" options={electorateOptions} value={formData.electorate} onChange={handleChange} onBlur={handleBlur} disabled={!formData.district} error={touched.electorate ? errors.electorate : undefined} data-id="element-23" />

                <FormSelect id="divisionalSecretariat" name="divisionalSecretariat" label="Divisional Secretariat | ප්‍රාදේශීය ලේකම් බල ප්‍රදේශය" placeholder="Select DS Division" options={dsOptions} value={formData.divisionalSecretariat} onChange={handleChange} onBlur={handleBlur} disabled={!formData.electorate} error={touched.divisionalSecretariat ? errors.divisionalSecretariat : undefined} data-id="element-24" />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-2 border-t border-slate-100" data-id="element-25">
                <FormInput id="gramaNiladhari" name="gramaNiladhari" label="Grama Niladhari Division | ග්‍රාම නිලධාරී වසම (Optional)" placeholder="e.g. GN 123" value={formData.gramaNiladhari} onChange={handleChange} onBlur={handleBlur} data-id="element-26" />

                <FormInput id="divisionalSecretariatText" name="divisionalSecretariatText" label="Divisional Secretariat (Optional Text)" placeholder="Enter DS Division" value={formData.divisionalSecretariatText} onChange={handleChange} onBlur={handleBlur} data-id="element-27" />
              </div>
            </div>
          </FormCard>

          {/* Section 3: Professional */}
          <FormCard title="Professional Info" description="Tell us about your current role." isValid={isProfessionalValid} data-id="element-28">
            <FormInput id="occupation" name="occupation" label="Current Occupation / රැකියාව" placeholder="e.g. Software Engineer" value={formData.occupation} onChange={handleChange} onBlur={handleBlur} error={touched.occupation ? errors.occupation : undefined} data-id="element-29" />
          </FormCard>

          {/* Submit Action */}
          <div className="pt-4 flex justify-end" data-id="element-30">
            <button type="submit" disabled={!isFormValid || isSubmitting} className={`
                flex items-center justify-center gap-2 px-8 py-3 rounded-lg font-semibold text-white shadow-lg
                transition-all duration-200
                ${isFormValid && !isSubmitting ? 'bg-blue-600 hover:bg-blue-700 hover:shadow-blue-200 hover:-translate-y-0.5' : 'bg-slate-300 cursor-not-allowed shadow-none'}
              `} data-id="element-31">
              {isSubmitting ? <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-32" />
                  <span data-id="element-33">Processing...</span>
                </> : <>
                  <span data-id="element-34">Complete Registration</span>
                  <Send size={18} data-id="element-35" />
                </>}
            </button>
          </div>
        </form>
      </div>
    </div>;
}